<template>
    <v-dialog v-model="dialogVisible" max-width="900px" persistent>
        <v-card>
            <v-card-title class="primary white--text">
                <v-icon left color="white">mdi-file-document</v-icon>
                Náhľad investičnej zmluvy
                <v-spacer></v-spacer>
                <v-btn icon color="white" @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-6">
                <!-- Upozornenie o stave zmluvy -->
                <v-alert type="warning" outlined class="mb-4">
                    <v-row align="center">
                        <v-col>
                            <strong>DÁTUM ÚČINNOSTI A PLATNOSTI ZMLUVY:</strong>
                            <span class="red--text ml-2">ZMLUVA NIE JE PLATNÁ</span>
                        </v-col>
                    </v-row>
                    <div class="mt-2 text-caption">
                        Zmluva sa stáva platnou až po pripísaní investovanej sumy na náš účet v banke.
                    </div>
                </v-alert>

                <!-- Obsah zmluvy -->
                <div class="contract-content">
                    <!-- 1. Hlavička -->
                    <div class="text-center mb-6">
                        <h2 class="text-h4 primary--text mb-2">INVESTIČNÁ ZMLUVA</h2>
                        <div class="text-h6 grey--text">č. {{ contractNumber }}</div>
                    </div>

                    <!-- 2. Špecifikácia investície -->
                    <v-card outlined class="mb-4">
                        <v-card-subtitle class="primary--text font-weight-bold">
                            I. ŠPECIFIKÁCIA INVESTÍCIE
                        </v-card-subtitle>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-list dense>
                                        <v-list-item>
                                            <v-list-item-content>
                                                <v-list-item-title>Investor:</v-list-item-title>
                                                <v-list-item-subtitle class="text-body-1">
                                                    {{ userInfo.first_name }} {{ userInfo.last_name }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-list-item>
                                            <v-list-item-content>
                                                <v-list-item-title>Adresa:</v-list-item-title>
                                                <v-list-item-subtitle class="text-body-1">
                                                    {{ userInfo.address || 'Adresa nebola zadaná' }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-list-item>
                                            <v-list-item-content>
                                                <v-list-item-title>Email:</v-list-item-title>
                                                <v-list-item-subtitle class="text-body-1">
                                                    {{ userInfo.email }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-list>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-list dense>
                                        <v-list-item>
                                            <v-list-item-content>
                                                <v-list-item-title>Investovaná suma:</v-list-item-title>
                                                <v-list-item-subtitle class="text-h6 success--text">
                                                    {{ formatCurrency(investmentAmount) }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-list-item>
                                            <v-list-item-content>
                                                <v-list-item-title>Koeficient násobku:</v-list-item-title>
                                                <v-list-item-subtitle class="text-h6 primary--text">
                                                    {{ displayCoefficient }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-list-item>
                                            <v-list-item-content>
                                                <v-list-item-title>Nárok na vyplatenie:</v-list-item-title>
                                                <v-list-item-subtitle class="text-h6 warning--text">
                                                    {{ formatCurrency(payoutClaim) }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-list>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>

                    <!-- Rozdelenie na viacero zmlúv (ak je potrebné) -->
                    <v-card v-if="multipleContracts" outlined class="mb-4" color="info lighten-5">
                        <v-card-subtitle class="info--text font-weight-bold">
                            <v-icon left small color="info">mdi-file-multiple</v-icon>
                            ROZDELENIE INVESTÍCIE
                        </v-card-subtitle>
                        <v-card-text>
                            <div class="text-body-2 mb-3">
                                Vaša investícia bude rozdelená na {{ investmentBreakdown.length }} zmluvy kvôli
                                optimalizácii koeficientov:
                            </div>
                            <v-simple-table>
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th>Zmluva</th>
                                            <th>Suma</th>
                                            <th>Koeficient</th>
                                            <th>Nárok</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(part, index) in investmentBreakdown" :key="index">
                                            <td>{{ contractNumber }}-{{ index + 1 }}</td>
                                            <td><strong>{{ formatCurrency(part.amount) }}</strong></td>
                                            <td><v-chip small color="success">{{ part.coefficient }}x</v-chip></td>
                                            <td class="success--text"><strong>{{ formatCurrency(part.payout) }}</strong>
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </v-card-text>
                    </v-card>

                    <!-- 3. Telo zmluvy -->
                    <v-card outlined class="mb-4">
                        <v-card-subtitle class="primary--text font-weight-bold">
                            II. PODMIENKY INVESTÍCIE
                        </v-card-subtitle>
                        <v-card-text>
                            <div class="text-body-1 line-height-1-6">
                                <p><strong>1. Predmet zmluvy:</strong></p>
                                <p>Investor sa zaväzuje poskytnúť finančné prostriedky vo výške {{
                                    formatCurrency(investmentAmount) }}
                                    na podporu rozvoja podnikateľských aktivít spoločnosti.</p>

                                <p><strong>2. Výnos z investície:</strong></p>
                                <p>Investor má nárok na vyplatenie sumy {{ formatCurrency(payoutClaim) }},
                                    čo predstavuje {{ displayCoefficient }} investovanej sumy.</p>

                                <p><strong>3. Spôsob vyplácania:</strong></p>
                                <p>Výnosy budú vyplácané z mesačných tržieb spoločnosti podľa aktuálne platného
                                    percentuálneho podielu určeného spoločnosťou (prednastavene 60%, možnosť úpravy
                                    60-70%).</p>

                                <p><strong>4. Variabilné ID:</strong></p>
                                <p>Pre identifikáciu platby používajte variabilný symbol: <strong>{{ userInfo.id
                                }}</strong></p>

                                <p><strong>5. Účinnosť zmluvy:</strong></p>
                                <p>Zmluva nadobúda účinnosť dňom pripísania investovanej sumy na účet spoločnosti.</p>

                                <p><strong>6. Ukončenie zmluvy:</strong></p>
                                <p>Zmluva sa ukončuje vyplatením celej sumy nároku investora podľa bodu 2.</p>

                                <p><strong>7. Výnimočné ukončenie:</strong></p>
                                <p>Ak by sa akýkoľvek z projektov uvedených v tejto zmluve alebo jeho podiel predal a to
                                    tak,
                                    že Boris Wagner nebude mať v tejto spoločnosti minimálne 51% podiel, tak v takomto
                                    prípade
                                    musia byť vysporiadané všetky závazky voči všetkým investorom.</p>
                            </div>
                        </v-card-text>
                    </v-card>

                    <!-- 4. Platobné údaje -->
                    <v-card outlined class="mb-4" color="success lighten-5">
                        <v-card-subtitle class="success--text font-weight-bold">
                            III. PLATOBNÉ ÚDAJE
                        </v-card-subtitle>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-list dense>
                                        <v-list-item>
                                            <v-list-item-content>
                                                <v-list-item-title>IBAN:</v-list-item-title>
                                                <v-list-item-subtitle class="text-h6">
                                                    {{ bankAccount.iban }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                            <v-list-item-action>
                                                <v-btn icon @click="copyToClipboard(bankAccount.iban)">
                                                    <v-icon>mdi-content-copy</v-icon>
                                                </v-btn>
                                            </v-list-item-action>
                                        </v-list-item>
                                        <v-list-item>
                                            <v-list-item-content>
                                                <v-list-item-title>Variabilný symbol:</v-list-item-title>
                                                <v-list-item-subtitle class="text-h6">
                                                    {{ userInfo.id }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                            <v-list-item-action>
                                                <v-btn icon @click="copyToClipboard(userInfo.id.toString())">
                                                    <v-icon>mdi-content-copy</v-icon>
                                                </v-btn>
                                            </v-list-item-action>
                                        </v-list-item>
                                    </v-list>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-list dense>
                                        <v-list-item>
                                            <v-list-item-content>
                                                <v-list-item-title>Suma na úhradu:</v-list-item-title>
                                                <v-list-item-subtitle class="text-h6 success--text">
                                                    {{ formatCurrency(investmentAmount) }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                            <v-list-item-action>
                                                <v-btn icon @click="copyToClipboard(investmentAmount.toString())">
                                                    <v-icon>mdi-content-copy</v-icon>
                                                </v-btn>
                                            </v-list-item-action>
                                        </v-list-item>
                                        <v-list-item>
                                            <v-list-item-content>
                                                <v-list-item-title>Príjemca:</v-list-item-title>
                                                <v-list-item-subtitle class="text-body-1">
                                                    {{ bankAccount.recipient }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-list>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>

                    <!-- 5. Dôležité upozornenia -->
                    <v-alert type="info" outlined class="mb-4">
                        <div class="font-weight-bold mb-2">Dôležité informácie:</div>
                        <ul class="ml-4">
                            <li>Výsledok v tejto investícii je len informatívny a nie je pre nikoho nijako záväzný.</li>
                            <li>Investícia sa stáva platnou až po pripísaní sumy na náš účet v banke.</li>
                            <li>Môže trvať až jeden pracovný deň, kým sa investícia prejaví v systéme.</li>
                            <li>Investovať môžete len násobky 10€, minimálna investícia je 10€.</li>
                            <li>Spoločnosť si vyhradzuje právo kedykoľvek zastaviť príjmanie týchto investícií.</li>
                        </ul>
                    </v-alert>

                    <!-- 6. Podpis -->
                    <v-row class="mt-6">
                        <v-col cols="6">
                            <div class="text-center">
                                <div class="mb-4">_________________________</div>
                                <div class="text-caption">Podpis investora</div>
                                <div class="text-body-2">{{ userInfo.first_name }} {{ userInfo.last_name }}</div>
                            </div>
                        </v-col>
                        <v-col cols="6">
                            <div class="text-center">
                                <div class="mb-4">_________________________</div>
                                <div class="text-caption">Za spoločnosť</div>
                                <div class="text-body-2">{{ companyRepresentative }}</div>
                            </div>
                        </v-col>
                    </v-row>
                </div>
            </v-card-text>

            <v-card-actions class="pa-6">
                <v-btn color="grey" outlined @click="closeDialog">
                    <v-icon left>mdi-close</v-icon>
                    Zavrieť
                </v-btn>

                <v-btn color="info" outlined @click="downloadContract">
                    <v-icon left>mdi-download</v-icon>
                    Stiahnuť PDF
                </v-btn>

                <v-spacer></v-spacer>

                <v-btn color="success" large @click="confirmInvestment" :loading="loading">
                    <v-icon left>mdi-check</v-icon>
                    Súhlasím a chcem investovať
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Confirmation Dialog -->
        <v-dialog v-model="confirmationDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title class="warning white--text">
                    <v-icon left color="white">mdi-alert</v-icon>
                    Potvrdenie investície
                </v-card-title>
                <v-card-text class="pa-4">
                    <div class="text-body-1 mb-4">
                        Ste si istý, že chcete investovať sumu <strong>{{ formatCurrency(investmentAmount) }}</strong>?
                    </div>

                    <v-alert type="info" outlined>
                        Po potvrdení bude vytvorená zmluva a budete presmerovaný na platbu.
                        Investícia sa stane aktívnou až po pripísaní sumy na náš účet.
                    </v-alert>

                    <div class="mt-4">
                        <v-checkbox v-model="agreementChecked" :rules="[v => !!v || 'Musíte súhlasiť s podmienkami']">
                            <template v-slot:label>
                                <div>
                                    Súhlasím s <a href="#" @click.stop="showTerms = true">obchodnými podmienkami</a>
                                    a potvrdzujem, že som si prečítal všetky dôležité informácie o investícii.
                                </div>
                            </template>
                        </v-checkbox>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="grey" @click="confirmationDialog = false">
                        Zrušiť
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="success" :disabled="!agreementChecked" @click="finalConfirmInvestment"
                        :loading="loading">
                        Definitívne potvrdiť
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Terms Dialog -->
        <v-dialog v-model="showTerms" max-width="600px">
            <v-card>
                <v-card-title>Obchodné podmienky</v-card-title>
                <v-card-text style="max-height: 400px; overflow-y: auto;">
                    <div class="text-body-2">
                        <h3>1. Všeobecné ustanovenia</h3>
                        <p>Tieto obchodné podmienky upravujú vzťahy medzi investorom a spoločnosťou pri investičných
                            aktivitách.
                        </p>

                        <h3>2. Podmienky investície</h3>
                        <p>Minimálna investícia je 10€. Investovať môžete len násobky 10€. Maximálna investícia je
                            1,000,000€.
                        </p>

                        <h3>3. Koeficienty návratnosti</h3>
                        <p>Koeficienty sú nastavené podľa úrovní investícií: 5x, 4x, 3x, 2x, 1.5x v závislosti od
                            celkovej výšky
                            investícií.</p>

                        <h3>4. Riziká investície</h3>
                        <p>Investícia môže priniesť stratu. Investujte len toľko, koľko si môžete dovoliť stratiť.
                            Výnosy
                            závisia od reálnych tržieb spoločnosti.</p>

                        <h3>5. Spôsob vyplácania</h3>
                        <p>Výnosy sú závislé od reálnych tržieb spoločnosti a vyplácajú sa percentuálne podľa podielu
                            investície.</p>

                        <h3>6. Zmeny a ukončenie</h3>
                        <p>Spoločnosť si vyhradzuje právo kedykoľvek zastaviť príjmanie investícií. V prípade predaja
                            projektov
                            musia byť všetky závazky vysporiadané.</p>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="showTerms = false">Zavrieť</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
export default {
    name: "ContractPreviewDialog",

    props: {
        value: {
            type: Boolean,
            default: false
        },
        investmentAmount: {
            type: Number,
            default: 0
        },
        userInfo: {
            type: Object,
            default: () => ({
                id: 1,
                first_name: '',
                last_name: '',
                email: '',
                address: ''
            })
        },
        investmentBreakdown: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            loading: false,
            confirmationDialog: false,
            agreementChecked: false,
            showTerms: false,

            // Bank account details - v reálnej aplikácii by prišlo z backendu
            bankAccount: {
                iban: 'SK89 1100 0000 0026 2900 0005',
                recipient: 'NAŠEJ SPOLOČNOSTI s.r.o.'
            },

            companyRepresentative: 'Boris Wagner, konateľ'
        };
    },

    computed: {
        dialogVisible: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },

        contractNumber() {
            // Formát podľa komentárov: userID/poradové číslo zmluvy
            const contractSequence = this.getNextContractSequence();
            return `${this.userInfo.id}/${contractSequence}`;
        },

        multipleContracts() {
            return this.investmentBreakdown && this.investmentBreakdown.length > 1;
        },

        displayCoefficient() {
            if (this.multipleContracts) {
                return 'Mix koeficientov';
            }
            if (this.investmentBreakdown && this.investmentBreakdown.length > 0) {
                return this.investmentBreakdown[0].coefficient + 'x';
            }
            return '5x'; // Default
        },

        payoutClaim() {
            if (this.investmentBreakdown && this.investmentBreakdown.length > 0) {
                return this.investmentBreakdown.reduce((sum, part) => sum + part.payout, 0);
            }
            return this.investmentAmount * 5; // Default koeficient
        }
    },

    methods: {
        getNextContractSequence() {
            // V reálnej aplikácii by sa načítalo z API
            // Pre demo používame timestamp
            return new Date().getTime().toString().slice(-3);
        },

        closeDialog() {
            this.dialogVisible = false;
            this.confirmationDialog = false;
            this.agreementChecked = false;
            this.showTerms = false;
        },

        confirmInvestment() {
            this.confirmationDialog = true;
        },

        finalConfirmInvestment() {
            if (!this.agreementChecked) return;

            this.loading = true;

            const investmentData = {
                amount: this.investmentAmount,
                breakdown: this.investmentBreakdown,
                payout_claim: this.payoutClaim,
                contract_number: this.contractNumber,
                user_info: this.userInfo,
                bank_account: this.bankAccount
            };

            this.$emit('confirm', investmentData);

            // Reset state
            setTimeout(() => {
                this.loading = false;
                this.confirmationDialog = false;
                this.agreementChecked = false;
                this.closeDialog();
            }, 1000);
        },

        downloadContract() {
            // V reálnej aplikácii by sa generoval a stiahol PDF
            console.log('Download contract PDF');

            // Pre demo - simulácia sťahovania
            const element = document.createElement('a');
            const content = this.generateContractText();
            const file = new Blob([content], { type: 'text/plain' });
            element.href = URL.createObjectURL(file);
            element.download = `zmluva_${this.contractNumber.replace('/', '_')}.txt`;
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
        },

        generateContractText() {
            return `
INVESTIČNÁ ZMLUVA č. ${this.contractNumber}

I. ŠPECIFIKÁCIA INVESTÍCIE
Investor: ${this.userInfo.first_name} ${this.userInfo.last_name}
Email: ${this.userInfo.email}
Adresa: ${this.userInfo.address || 'Adresa nebola zadaná'}

Investovaná suma: ${this.formatCurrency(this.investmentAmount)}
Koeficient násobku: ${this.displayCoefficient}
Nárok na vyplatenie: ${this.formatCurrency(this.payoutClaim)}

II. PODMIENKY INVESTÍCIE
1. Predmet zmluvy: Investor sa zaväzuje poskytnúť finančné prostriedky vo výške ${this.formatCurrency(this.investmentAmount)} na podporu rozvoja podnikateľských aktivít spoločnosti.

2. Výnos z investície: Investor má nárok na vyplatenie sumy ${this.formatCurrency(this.payoutClaim)}, čo predstavuje ${this.displayCoefficient} investovanej sumy.

3. Spôsob vyplácania: Výnosy budú vyplácané z mesačných tržieb spoločnosti podľa aktuálne platného percentuálneho podielu určeného spoločnosťou.

4. Variabilné ID: ${this.userInfo.id}

5. Účinnosť zmluvy: Zmluva nadobúda účinnosť dňom pripísania investovanej sumy na účet spoločnosti.

III. PLATOBNÉ ÚDAJE
IBAN: ${this.bankAccount.iban}
Variabilný symbol: ${this.userInfo.id}
Suma na úhradu: ${this.formatCurrency(this.investmentAmount)}
Príjemca: ${this.bankAccount.recipient}

DÔLEŽITÉ: Výsledok v tejto investícii je len informatívny a nie je pre nikoho nijako záväzný.

Podpis investora: ${this.userInfo.first_name} ${this.userInfo.last_name}
Za spoločnosť: ${this.companyRepresentative}

Dátum vytvorenia náhľadu: ${new Date().toLocaleDateString('sk-SK')}
      `.trim();
        },

        copyToClipboard(text) {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(() => {
                    // V reálnej aplikácii by tu bol toast
                    console.log('Skopírované do schránky:', text);
                }).catch(() => {
                    console.error('Nepodarilo sa skopírovať');
                });
            } else {
                // Fallback pre staršie prehliadače
                const textArea = document.createElement('textarea');
                textArea.value = text;
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                try {
                    document.execCommand('copy');
                    console.log('Skopírované do schránky:', text);
                } catch (err) {
                    console.error('Nepodarilo sa skopírovať');
                }
                document.body.removeChild(textArea);
            }
        },

        formatCurrency(amount) {
            if (typeof amount !== 'number') return '0€';
            return new Intl.NumberFormat('sk-SK', {
                style: 'currency',
                currency: 'EUR'
            }).format(amount);
        }
    }
};
</script>

<style scoped>
.contract-content {
    font-family: 'Times New Roman', serif;
    line-height: 1.6;
}

.line-height-1-6 {
    line-height: 1.6;
}

.contract-content h2 {
    margin-bottom: 1rem;
}

.contract-content p {
    margin-bottom: 0.8rem;
    text-align: justify;
}

.contract-content ul {
    margin-bottom: 1rem;
}

.contract-content li {
    margin-bottom: 0.4rem;
}

.v-card>>>.v-card__subtitle {
    padding-bottom: 8px;
}

.signature-area {
    border-top: 1px solid #000;
    margin-top: 2rem;
    padding-top: 0.5rem;
}

/* Print styles */
@media print {
    .v-dialog {
        position: static !important;
    }

    .v-card-actions {
        display: none !important;
    }

    .v-alert {
        border: 1px solid #ccc !important;
    }
}

/* Mobile responsive */
@media (max-width: 600px) {
    .contract-content {
        font-size: 0.9rem;
    }

    .text-h4 {
        font-size: 1.5rem !important;
    }

    .text-h6 {
        font-size: 1.1rem !important;
    }
}
</style>